/**
 * LifeWithNewSystemCard Component
 *
 * Paints an EMOTIONAL VISION of life with a new system.
 * Addresses each comfort issue they reported and shows the relief.
 * "Sell the benefit, not the feature" - what will their life be like?
 */

export default function LifeWithNewSystemCard({ issues }) {
  // Benefits of new system addressing each reported issue
  const benefits = [];

  if (issues.hotCold) {
    benefits.push({
      icon: '✨',
      title: 'Consistent Comfort Throughout Your Home',
      description: 'Every room will maintain the perfect temperature. No more hot spots in summer or cold rooms in winter. Modern systems are precisely sized and balanced for even, comfortable air distribution.'
    });
  }

  if (issues.humidity) {
    benefits.push({
      icon: '💨',
      title: 'Perfect Humidity Control Year-Round',
      description: 'Advanced systems manage humidity levels automatically. Your home will feel fresher in summer without that sticky feeling, and your skin won\'t dry out in winter. Better air quality means better sleep and health.'
    });
  }

  if (issues.runsConstantly || issues.tempTrouble) {
    benefits.push({
      icon: '🎯',
      title: 'Efficient, Responsive Temperature Control',
      description: 'A properly sized system reaches your desired temperature quickly and maintains it effortlessly. Instead of running constantly, it cycles normally and efficiently, saving energy while keeping you comfortable.'
    });
  }

  if (issues.highBills) {
    benefits.push({
      icon: '💰',
      title: 'Dramatically Lower Energy Bills',
      description: 'Modern high-efficiency systems use a fraction of the energy of older units. You\'ll see the savings every single month on your utility bills, often covering a significant portion of your payment.'
    });
  }

  if (issues.loud) {
    benefits.push({
      icon: '🔇',
      title: 'Whisper-Quiet Operation',
      description: 'Today\'s systems are engineered for near-silent operation. No more disruptive rattling, banging, or noise. You\'ll barely know it\'s running, creating a peaceful environment for relaxation and sleep.'
    });
  }

  // Always show these universal benefits
  const universalBenefits = [
    {
      icon: '🛡️',
      title: 'Peace of Mind with Warranty Coverage',
      description: 'New systems come with comprehensive manufacturer warranties covering parts and often labor. No more unexpected repair bills or emergency breakdowns leaving you without heat or AC.'
    },
    {
      icon: '🌿',
      title: 'Environmental Responsibility',
      description: 'Modern systems use eco-friendly refrigerants and are dramatically more efficient, reducing your carbon footprint while saving you money. It\'s good for your wallet and good for the planet.'
    }
  ];

  const allBenefits = [...benefits, ...universalBenefits];

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '30px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      marginBottom: '25px'
    }}>
      {/* Header */}
      <div style={{
        borderBottom: '3px solid #8b5cf6',
        paddingBottom: '15px',
        marginBottom: '25px'
      }}>
        <div style={{
          fontSize: '13px',
          fontWeight: '700',
          color: '#8b5cf6',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          marginBottom: '8px'
        }}>
          Imagine Your Home With a New System
        </div>
        <h3 style={{
          margin: 0,
          fontSize: '24px',
          color: '#1e293b',
          fontWeight: '700'
        }}>
          Life After Replacement
        </h3>
        <p style={{
          margin: '8px 0 0 0',
          fontSize: '15px',
          color: '#64748b',
          lineHeight: '1.5'
        }}>
          Here's what you can look forward to with a modern, properly sized HVAC system:
        </p>
      </div>

      {/* Benefits List */}
      <div style={{
        display: 'grid',
        gap: '20px',
        marginBottom: '25px'
      }}>
        {allBenefits.map((benefit, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              padding: '20px',
              background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
              borderRadius: '10px',
              border: '2px solid #e9d5ff'
            }}
          >
            {/* Icon */}
            <div style={{
              fontSize: '36px',
              marginRight: '18px',
              flexShrink: 0
            }}>
              {benefit.icon}
            </div>

            {/* Content */}
            <div>
              <h4 style={{
                margin: '0 0 8px 0',
                fontSize: '18px',
                fontWeight: '700',
                color: '#1e293b'
              }}>
                {benefit.title}
              </h4>
              <p style={{
                margin: 0,
                fontSize: '14px',
                color: '#64748b',
                lineHeight: '1.6'
              }}>
                {benefit.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Transformation Summary */}
      <div style={{
        padding: '25px',
        background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
        borderRadius: '12px',
        border: '2px solid #93c5fd'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '20px'
        }}>
          <div style={{ fontSize: '48px', flexShrink: 0 }}>🏡</div>
          <div>
            <h4 style={{
              margin: '0 0 10px 0',
              fontSize: '20px',
              fontWeight: '700',
              color: '#1e3a8a'
            }}>
              Transform Your Home Comfort Experience
            </h4>
            <p style={{
              margin: 0,
              fontSize: '15px',
              color: '#1e40af',
              lineHeight: '1.6'
            }}>
              A new HVAC system isn't just about heating and cooling—it's about creating a home environment
              where you and your family are consistently comfortable, healthy, and worry-free. Modern technology
              has transformed home comfort, and you deserve to experience the difference.
            </p>
          </div>
        </div>
      </div>

      {/* Emotional Call to Action */}
      <div style={{
        marginTop: '20px',
        padding: '15px 20px',
        background: '#f0fdf4',
        borderLeft: '4px solid #10b981',
        borderRadius: '6px'
      }}>
        <p style={{
          margin: 0,
          fontSize: '14px',
          color: '#065f46',
          lineHeight: '1.6'
        }}>
          <strong>💚 The Bottom Line:</strong> You spend most of your life in your home. Doesn't it make sense
          to invest in your comfort, health, and peace of mind? The benefits go far beyond just dollars and cents—
          they impact your daily quality of life.
        </p>
      </div>
    </div>
  );
}
